import { motion } from 'framer-motion';
import { CheckCircle, Mail, Phone, User } from 'lucide-react';
import React from 'react';
import ImageWithPlaceholder from '../../../shared/components/ImageWithPlaceholderProps';
import { Provider } from '../../../provider/types';


// Define default colors to use if not provided
const defaultColors = {
  cardBackground: 'rgba(0, 0, 0, 0.95)',
  cardBorder: '#DDB88919',
  cardBorderSelected: "#22c55e49",
  cardShadow: 'rgba(221,184,137,0.07)',
  cardShadowSelected: "rgba(34,197,94,0.25)",
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  iconColor: '#DDB889B2',
  selectionBackground: "#22c55e",
  selectionIcon: "#FFFFFF"
};
interface ProviderCardProps {
  providers: { node: Provider }[];
  isClickable?: boolean;
  onProviderClick?: (provider: Provider) => void;
  selectedProviders?: Provider | Provider[] | null;
  showPhotos?: boolean;
  showAvailability?: boolean;
  colors?: {
    cardBackground?: string;
    cardBorder?: string;
    cardBorderSelected?: string;
    cardShadow?: string;
    cardShadowSelected?: string;
    titleText?: string;
    subtitleText?: string;
    iconColor?: string;
    selectionBackground?: string;
    selectionIcon?: string;
  };
  itemsPerRow?: number;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  providers,
  isClickable = true,
  onProviderClick,
  selectedProviders = null,
  showPhotos = true,
  showAvailability = true,
  colors = {},
  itemsPerRow = 3
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const providerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        stiffness: 100
      }
    }
  };

  const isProviderSelected = (provider: Provider) => {
    if (!selectedProviders) return false;
    if (Array.isArray(selectedProviders)) {
      return selectedProviders.some(selected => selected.id === provider.id);
    }
    return selectedProviders.id === provider.id;
  };

  const handleProviderClick = (provider: Provider) => {
    if (!isClickable || !onProviderClick) return;
    onProviderClick(provider);
  };

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Determine grid layout class based on itemsPerRow
  const getGridLayoutClass = () => {
    switch (itemsPerRow) {
      case 1:
        return 'lp-grid-cols-1';
      case 2:
        return 'lp-grid-cols-1 md:lp-grid-cols-2';
      case 3:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-3';
      case 4:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-4';
      default:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-3';
    }
  };

      const getHoverShadowStyle = () => {
      return `0 0 20px ${themeColors.cardShadowSelected}`;
    };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`lp-grid lp-gap-6 ${getGridLayoutClass()}`}

    >
      {providers.map(({ node: provider }) => (
        <motion.div
  key={provider.id}
  variants={providerVariants}
  onClick={() => handleProviderClick(provider)}
  className={`
    lp-relative
    lp-backdrop-blur-sm
    lp-rounded-2xl
    lp-border
    lp-overflow-hidden
    lp-h-full
    lp-flex lp-flex-col
    lp-transition-all lp-duration-700
    ${isClickable ? 'lp-cursor-pointer' : ''}
  `}
  style={{
    backgroundColor: themeColors.cardBackground,
    borderColor: isProviderSelected(provider)
      ? themeColors.cardBorderSelected
      : themeColors.cardBorder,
    boxShadow: isProviderSelected(provider)
      ? `0 0 15px ${themeColors.cardShadowSelected}`
      : `0 0 20px ${themeColors.cardShadow}`
  }}
  whileHover={{
            boxShadow: getHoverShadowStyle()
 }}
>
          {showPhotos && (
            <div className="lp-relative lp-h-48 lp-w-full lp-flex lp-items-center lp-justify-center lp-overflow-hidden"
              style={{
                backgroundColor: themeColors.cardBackground
              }}
            >
              {provider.image ? (
                <ImageWithPlaceholder
                  image={provider.image}
                  alt={provider.firstName}
                  className="lp-w-full lp-h-full lp-object-cover"
                />
              ) : (
                <User
                  className="lp-w-12 lp-h-12"
                  style={{ color: `${themeColors.iconColor}33` }}
                />
              )}
              {isProviderSelected(provider) && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="lp-absolute lp-top-4 lp-right-4 lp-w-8 lp-h-8 lp-rounded-full
                    lp-flex lp-items-center lp-justify-center lp-transform hover:lp-rotate-180
                    lp-transition-transform lp-duration-300"
                  style={{ backgroundColor: themeColors.selectionBackground }}
                >
                  <CheckCircle
                    className="lp-w-5 lp-h-5"
                    style={{ color: themeColors.selectionIcon }}
                  />
                </motion.div>
              )}
            </div>
          )}

          <div className="lp-p-6 lp-flex lp-flex-col lp-flex-grow">
            <div className="lp-flex lp-items-start lp-justify-between lp-mb-4">
              <h3
                className="lp-text-xl lp-font-medium"
                style={{ color: themeColors.titleText }}
              >
                {capitalizeFirstLetter(provider.firstName)} {capitalizeFirstLetter(provider.lastName)}
              </h3>
            </div>

            <div className="lp-grid lp-grid-cols-1 lp-gap-4 lp-mt-auto">
              <div
                className="lp-flex lp-items-center lp-gap-2 hover:lp-opacity-100 lp-transition-colors lp-duration-200"
                style={{ color: `${themeColors.iconColor}CC` }}
              >
                <Phone className="lp-w-4 lp-h-4" />
                <span className="lp-text-sm">{provider.phone}</span>
              </div>
              <div
                className="lp-flex lp-items-center lp-gap-2 hover:lp-opacity-100 lp-transition-colors lp-duration-200"
                style={{ color: `${themeColors.iconColor}CC` }}
              >
                <Mail className="lp-w-4 lp-h-4" />
                <span className="lp-text-sm">{provider.email}</span>
              </div>

              {/* Availability info (optional) */}
              {showAvailability && provider.availability && (
                <div
                  className="lp-flex lp-items-center lp-gap-2 hover:lp-opacity-100 lp-transition-colors lp-duration-200 lp-mt-2"
                  style={{ color: `${themeColors.iconColor}CC` }}
                >
                  <span className="lp-text-sm">
                    {provider.availability.days}, {provider.availability.hours}
                  </span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default ProviderCard;
